/**
 * <sf-pdp> — Shower Filter PDP controller.
 *
 * Scoped entirely to this template. Handles gallery navigation, variant
 * selection, purchase-plan selection, info tabs/accordion state and cart
 * submission. Everything degrades to working HTML without JS: the gallery is a
 * scroll-snap track, variants are radio inputs inside the form, and the form
 * posts natively to /cart/add.
 */
class SfPdp extends HTMLElement {
  connectedCallback() {
    // Shopify's {% form %} tag renders the <form> itself, so query the element
    // rather than a data attribute we cannot reliably put on it.
    this.form = this.querySelector('form.sf-buy');
    this.variants = this.#json('[data-sf-variants]') || [];
    this.optionInputs = Array.from(this.querySelectorAll('[data-sf-option]'));
    this.planInputs = Array.from(this.querySelectorAll('[data-sf-plan]'));
    this.variantIdInput = this.querySelector('[data-sf-variant-id]');
    this.sellingPlanInput = this.querySelector('[data-sf-selling-plan]');

    this.#initGallery();
    this.#initOptions();
    this.#initPlans();
    this.#initInfo();
    this.#initForm();
  }

  #json(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /* ---------------- Gallery ---------------- */

  #initGallery() {
    this.track = this.querySelector('[data-sf-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.thumbs = Array.from(this.querySelectorAll('[data-sf-thumb]'));
    const prev = this.querySelector('[data-sf-prev]');
    const next = this.querySelector('[data-sf-next]');

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.#goToSlide(i)));
    prev?.addEventListener('click', () => this.#step(-1));
    next?.addEventListener('click', () => this.#step(1));

    // root: the scroller, so page scroll position never affects the active slide
    this.galleryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.#setActiveSlide(this.slides.indexOf(e.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((s) => this.galleryObserver.observe(s));
    this.#setActiveSlide(0);
  }

  #goToSlide(i) {
    const slide = this.slides?.[i];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #step(delta) {
    const next = Math.min(Math.max((this.activeSlide ?? 0) + delta, 0), this.slides.length - 1);
    this.#goToSlide(next);
  }

  #setActiveSlide(i) {
    if (i < 0) return;
    this.activeSlide = i;
    // The thumbnail strip is the only position indicator now; the dots that
    // used to sit over the photo repeated what it already says.
    this.thumbs?.forEach((t, n) => {
      t.classList.toggle('is-active', n === i);
      t.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
    const prev = this.querySelector('[data-sf-prev]');
    const next = this.querySelector('[data-sf-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= this.slides.length - 1;
  }

  /* ---------------- Variants ---------------- */

  #initOptions() {
    if (!this.optionInputs.length) return;
    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.#onOptionChange()));
    this.#onOptionChange({ initial: true });
  }

  #selectedOptions() {
    const byIndex = [];
    this.optionInputs.forEach((input) => {
      if (input.checked) byIndex[Number(input.dataset.sfOptionIndex)] = input.value;
    });
    return byIndex;
  }

  #onOptionChange({ initial = false } = {}) {
    const selected = this.#selectedOptions();
    const match = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    if (!match) return;

    this.currentVariant = match;
    if (this.variantIdInput) this.variantIdInput.value = match.id;

    // Reflect the chosen option value next to the option name, e.g. "Color: Chrome"
    const label = this.querySelector('[data-sf-option-value]');
    if (label) label.textContent = selected[Number(label.dataset.sfOptionValue)] || '';

    this.#renderPrice();
    this.#renderAvailability();

    if (match.featured_media_index != null) this.#goToSlide(match.featured_media_index);

    if (!initial) {
      // Keep the URL shareable without adding history entries.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  #activePlan() {
    return this.planInputs.find((i) => i.checked) || null;
  }

  #renderPrice() {
    const v = this.currentVariant;
    if (!v) return;
    const plan = this.#activePlan();
    // A plan may carry its own price (subscription discount); fall back to variant.
    const price = plan?.dataset.sfPlanPrice ? Number(plan.dataset.sfPlanPrice) : v.price;
    const compare = plan?.dataset.sfPlanCompare ? Number(plan.dataset.sfPlanCompare) : v.compare_at_price;

    const ctaPrice = this.querySelector('[data-sf-cta-price]');
    const ctaCompare = this.querySelector('[data-sf-cta-compare]');
    if (ctaPrice) ctaPrice.textContent = this.#money(price);
    if (ctaCompare) {
      const show = compare && compare > price;
      ctaCompare.hidden = !show;
      if (show) ctaCompare.textContent = this.#money(compare);
    }
  }

  #renderAvailability() {
    const v = this.currentVariant;
    const btn = this.querySelector('[data-sf-submit]');
    if (!btn || !v) return;
    const soldOut = !v.available;
    btn.disabled = soldOut;
    btn.toggleAttribute('data-sold-out', soldOut);
    const label = btn.querySelector('[data-sf-cta-label]');
    if (label) label.textContent = soldOut ? btn.dataset.soldOutLabel || 'Sold out' : btn.dataset.defaultLabel || 'Add to Cart';
  }

  /* ---------------- Purchase plans ---------------- */

  #initPlans() {
    if (!this.planInputs.length) return;
    this.planInputs.forEach((i) => i.addEventListener('change', () => this.#onPlanChange()));
    this.#onPlanChange();
  }

  #onPlanChange() {
    const plan = this.#activePlan();
    this.planInputs.forEach((i) => {
      i.closest('[data-sf-plan-card]')?.classList.toggle('is-selected', i.checked);
    });

    if (this.sellingPlanInput) {
      // Only attach a selling plan when the chosen option actually maps to one.
      // Never guess: an unmapped subscription option submits as one-time, and
      // the card itself is flagged in the editor.
      const id = plan?.dataset.sfSellingPlan || '';
      this.sellingPlanInput.value = id;
      this.sellingPlanInput.disabled = !id;
    }

    this.#renderPrice();
  }

  /* ---------------- Info tabs / accordion ---------------- */

  #initInfo() {
    this.infoRoot = this.querySelector('[data-sf-info]');
    if (!this.infoRoot) return;
    this.infoTabs = Array.from(this.infoRoot.querySelectorAll('[data-sf-tab]'));
    this.infoPanels = Array.from(this.infoRoot.querySelectorAll('[data-sf-panel]'));

    this.infoTabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        const accordion = this.infoRoot.dataset.mode === 'accordion';
        if (accordion) {
          const open = tab.getAttribute('aria-expanded') === 'true';
          tab.setAttribute('aria-expanded', String(!open));
          this.infoPanels[i].hidden = open;
        } else {
          this.#selectTab(i);
        }
      });

      tab.addEventListener('keydown', (e) => {
        if (this.infoRoot.dataset.mode === 'accordion') return;
        const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        const n = (i + dir + this.infoTabs.length) % this.infoTabs.length;
        this.infoTabs[n].focus();
        this.#selectTab(n);
      });
    });

    this.mql = window.matchMedia('(min-width: 750px)');
    this.applyInfoMode = () => this.#setInfoMode(this.mql.matches ? 'tabs' : 'accordion');
    this.mql.addEventListener('change', this.applyInfoMode);
    this.applyInfoMode();
  }

  #setInfoMode(mode) {
    if (this.infoRoot.dataset.mode === mode) return;
    this.infoRoot.dataset.mode = mode;
    if (mode === 'tabs') {
      this.infoTabs.forEach((t) => t.setAttribute('role', 'tab'));
      this.#selectTab(0);
    } else {
      // Accordion: first panel open, rest collapsed, no tab semantics.
      this.infoTabs.forEach((t, i) => {
        t.removeAttribute('role');
        t.removeAttribute('aria-selected');
        t.setAttribute('aria-expanded', String(i === 0));
      });
      this.infoPanels.forEach((p, i) => {
        p.hidden = i !== 0;
      });
    }
  }

  #selectTab(index) {
    this.infoTabs.forEach((t, i) => {
      t.setAttribute('aria-selected', String(i === index));
      t.removeAttribute('aria-expanded');
      t.tabIndex = i === index ? 0 : -1;
    });
    this.infoPanels.forEach((p, i) => {
      p.hidden = i !== index;
    });
  }

  /* ---------------- Cart ---------------- */

  #initForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => {
      const btn = this.querySelector('[data-sf-submit]');
      if (!btn) return;
      if (this.submitting) {
        e.preventDefault();
        return;
      }
      this.submitting = true;
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      // Native navigation follows; re-enable in case the browser restores the
      // page from bfcache rather than reloading it.
      window.addEventListener(
        'pageshow',
        () => {
          this.submitting = false;
          btn.removeAttribute('aria-busy');
          this.#renderAvailability();
        },
        { once: true }
      );
    });
  }

  disconnectedCallback() {
    this.galleryObserver?.disconnect();
    if (this.mql && this.applyInfoMode) this.mql.removeEventListener('change', this.applyInfoMode);
  }
}

if (!customElements.get('sf-pdp')) customElements.define('sf-pdp', SfPdp);
